import {
  ArchiveIcon,
  ChatIcon,
  DashboardIcon,
  DataFolderIcon,
  DocumentAnalysisSidebarIcon,
  OrganizationIcon,
  ShieldIcon,
  WorkflowIcon,
} from "@cynthai/icons";
import { useMemo } from "react";

import { useAppCapabilities } from "@/core/hooks/use-app-capabilities";
import { useCurrentUser } from "@/core/hooks/use-current-user";
import { isOrgOwner } from "@/core/lib/access";
import type { SidebarItem } from "@/core/lib/sidebar-types";
import { useLastConversation } from "@/core/lib/state-xstate";
import { buildProjectTypeSidebarItems } from "@/features/project/lib/sidebar-registry";
import { m } from "@/paraglide/messages";

export function useMenuItems() {
  const user = useCurrentUser();
  const appCapabilities = useAppCapabilities();
  const lastConversation = useLastConversation();

  return useMemo(() => {
    const items: SidebarItem[] = [
      {
        label: "CynthAI Chat",
        to: lastConversation.id
          ? {
              to: "/",
              search: {
                c: lastConversation.id,
                ds: lastConversation.ds,
                col: lastConversation.col,
              },
            }
          : "/",
        icon: ChatIcon,
      },
      { label: m.chat_history(), icon: ArchiveIcon, to: "/history" },
      {
        label: m.data_management(),
        icon: DataFolderIcon,
        to: "/data/datasources",
        activePathnamePrefix: "/data",
      },
      { label: m.dashboard(), icon: DashboardIcon, to: "/bi" },
    ];

    items.push(
      {
        label: m.document_analysis(),
        icon: DocumentAnalysisSidebarIcon,
        to: "/document-analysis",
      },
      {
        label: m.workflows(),
        icon: WorkflowIcon,
        to: "/workflows",
      },
      ...buildProjectTypeSidebarItems(
        user.project_type_memberships,
        appCapabilities,
      ),
    );

    if (user.organization && isOrgOwner(user)) {
      items.push({
        label: m.organization(),
        icon: OrganizationIcon,
        to: `/organization/${user.organization.id}`,
      });
    }

    if (user.is_superuser) {
      items.push({
        label: m.admin_panel(),
        icon: ShieldIcon,
        to: "/superuser",
        subItems: [
          { label: m.admin_customers(), to: "/superuser/customers" },
          { label: m.settings(), to: "/superuser/settings" },
          { label: m.admin_usage(), to: "/superuser/usage" },
        ],
      });
    }

    return items;
  }, [
    appCapabilities,
    lastConversation.col,
    lastConversation.ds,
    lastConversation.id,
    user,
  ]);
}
